/**
 * Truncates rich text by lines or words, with a Read more / Read less toggle.
 *
 * data-truncate-by: "lines" | "words"
 * data-limit: number
 *
 * Lines mode uses max-height (not -webkit-line-clamp) so multi-block RTE
 * content (p, ul, headings) truncates reliably.
 */
class WbsReadMore extends HTMLElement {
  /** @type {boolean} */
  #expanded = false;

  /** @type {string} */
  #originalHTML = '';

  /** @type {HTMLElement | null} */
  #content = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLElement | null} */
  #moreLabel = null;

  /** @type {HTMLElement | null} */
  #lessLabel = null;

  connectedCallback() {
    this.#content = this.querySelector('[ref="content"]');
    this.#button = this.querySelector('[ref="button"]');
    this.#moreLabel = this.querySelector('[ref="moreLabel"]');
    this.#lessLabel = this.querySelector('[ref="lessLabel"]');

    if (!this.#content || !this.#button) return;

    this.#originalHTML = this.#content.innerHTML;
    this.#button.addEventListener('click', this.#toggle);
    this.#syncLabels();
    this.#setup();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#toggle);
  }

  #setup = () => {
    const truncateBy = this.dataset.truncateBy === 'words' ? 'words' : 'lines';
    const limit = Math.max(1, Number(this.dataset.limit) || (truncateBy === 'words' ? 50 : 4));

    this.dataset.truncateBy = truncateBy;
    this.dataset.limit = String(limit);
    this.dataset.expanded = 'false';
    this.#expanded = false;
    this.#button?.setAttribute('aria-expanded', 'false');
    this.removeAttribute('data-disabled');

    if (this.#button) {
      this.#button.hidden = false;
      this.#button.removeAttribute('aria-hidden');
    }

    if (truncateBy === 'lines') {
      this.#setupLines(limit);
      return;
    }

    const wordCount = this.#countWords(this.#content);
    if (wordCount <= limit) {
      this.#disable();
      return;
    }

    this.#applyWordTruncate(limit);
    this.#content?.classList.add('is-collapsed');
    this.#content?.setAttribute('data-truncate-by', 'words');
  };

  /**
   * @param {number} limit
   */
  #setupLines = (limit) => {
    if (!this.#content) return;

    // Measure full height without clamp first.
    this.#content.classList.remove('is-collapsed');
    this.#content.removeAttribute('data-truncate-by');
    this.#content.style.removeProperty('--read-more-max-height');
    this.#content.style.removeProperty('max-height');

    const fullHeight = this.#content.scrollHeight;
    const lineHeight = this.#getLineHeight(this.#content);
    const maxHeight = Math.max(lineHeight, lineHeight * limit);

    if (fullHeight <= maxHeight + 2) {
      this.#disable();
      return;
    }

    this.#content.style.setProperty('--read-more-max-height', `${maxHeight}px`);
    this.#content.setAttribute('data-truncate-by', 'lines');
    this.#content.classList.add('is-collapsed');
  };

  /**
   * @param {Element} root
   * @returns {number}
   */
  #getLineHeight = (root) => {
    const sample =
      root.querySelector('p, li, h1, h2, h3, h4, h5, h6, .text-block, rte-formatter') || root;
    const styles = window.getComputedStyle(sample);
    const lineHeight = Number.parseFloat(styles.lineHeight);

    if (!Number.isNaN(lineHeight) && styles.lineHeight !== 'normal') {
      return lineHeight;
    }

    const fontSize = Number.parseFloat(styles.fontSize) || 16;
    return fontSize * 1.5;
  };

  #disable = () => {
    this.#content?.classList.remove('is-collapsed');
    this.#content?.removeAttribute('data-truncate-by');
    this.#content?.style.removeProperty('--read-more-max-height');
    this.#content?.style.removeProperty('max-height');
    this.#content?.style.removeProperty('--read-more-lines');

    if (this.#button) {
      this.#button.hidden = true;
      this.#button.setAttribute('aria-hidden', 'true');
    }

    this.dataset.expanded = 'true';
    this.setAttribute('data-disabled', 'true');
  };

  /**
   * @param {ParentNode | null} root
   * @returns {number}
   */
  #countWords = (root) => {
    if (!root) return 0;
    return (root.textContent || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;
  };

  /**
   * @param {number} limit
   */
  #applyWordTruncate = (limit) => {
    if (!this.#content) return;

    this.#content.innerHTML = this.#originalHTML;

    const walker = document.createTreeWalker(this.#content, NodeFilter.SHOW_TEXT);
    let count = 0;
    /** @type {Text | null} */
    let truncateNode = null;
    /** @type {string} */
    let truncateText = '';

    /** @type {Node | null} */
    let node;
    while ((node = walker.nextNode())) {
      if (!(node instanceof Text)) continue;
      if (!node.textContent?.trim()) continue;

      const parts = node.textContent?.match(/\S+|\s+/g) || [];
      let nextText = '';
      let hitLimit = false;

      for (const part of parts) {
        if (/^\s+$/.test(part)) {
          if (!hitLimit) nextText += part;
          continue;
        }

        if (count >= limit) {
          hitLimit = true;
          break;
        }

        nextText += part;
        count += 1;
      }

      if (hitLimit) {
        truncateNode = node;
        truncateText = nextText.replace(/\s+$/, '') + '…';
        break;
      }
    }

    if (!truncateNode) return;

    truncateNode.textContent = truncateText;
    this.#removeNodesAfter(truncateNode);
    this.#pruneEmptyElements(this.#content);
  };

  /**
   * Removes every node that comes after `node` in document order,
   * walking up ancestors so later paragraphs/lists are dropped.
   * @param {Node} node
   */
  #removeNodesAfter = (node) => {
    let current = node;

    while (current && current !== this.#content) {
      let sibling = current.nextSibling;

      while (sibling) {
        const next = sibling.nextSibling;
        sibling.parentNode?.removeChild(sibling);
        sibling = next;
      }

      current = current.parentNode;
    }
  };

  /**
   * Drops leftover empty tags (e.g. empty li/ul/p/headings) after truncation.
   * @param {HTMLElement} root
   */
  #pruneEmptyElements = (root) => {
    const elements = [...root.querySelectorAll('*')].reverse();

    for (const el of elements) {
      if (el.matches('img, video, iframe, svg, br, hr, source, picture')) continue;
      if (el.querySelector('img, video, iframe, svg')) continue;

      const text = (el.textContent || '').replace(/\u2026/g, '…').trim();
      if (text === '') {
        el.remove();
      }
    }
  };

  #syncLabels = () => {
    if (this.#moreLabel) this.#moreLabel.hidden = this.#expanded;
    if (this.#lessLabel) this.#lessLabel.hidden = !this.#expanded;
  };

  /**
   * @param {Event} event
   */
  #toggle = (event) => {
    event.preventDefault();
    if (this.hasAttribute('data-disabled') || !this.#content || !this.#button) return;

    this.#expanded = !this.#expanded;
    this.dataset.expanded = String(this.#expanded);
    this.#button.setAttribute('aria-expanded', String(this.#expanded));
    this.#syncLabels();

    const truncateBy = this.dataset.truncateBy === 'words' ? 'words' : 'lines';
    const limit = Math.max(1, Number(this.dataset.limit) || 4);

    if (truncateBy === 'words') {
      if (this.#expanded) {
        this.#content.innerHTML = this.#originalHTML;
        this.#content.classList.remove('is-collapsed');
      } else {
        this.#applyWordTruncate(limit);
        this.#content.classList.add('is-collapsed');
      }
      return;
    }

    this.#content.classList.toggle('is-collapsed', !this.#expanded);
  };
}

if (!customElements.get('wbs-read-more')) {
  customElements.define('wbs-read-more', WbsReadMore);
}
